import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import EmptyState from "../../../../components/EmptyState";
import { SectionLoader } from "../../../../components/PageLoader";
import CustomButton from "../../../../components/CustomButton";
import CustomInput from "../../../../components/CustomInput";
import {
  FormControl,
  MenuItem,
  Select,
  DialogContent,
  Dialog,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import {
  getBanksList,
  createBankAccount,
  getBankAccount,
  getMyAccounts,
  resetMyAccounts,
  withdrawFromWallet,
} from "../../../../redux/actions/walletActions";
import Account from "../Add/Account";
import StripeAddAccount from "./StripeAddAccount";

const Withdraw = ({ type }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [addingAccount, setAddingAccount] = useState(false);
  const [accountDetails, setAccountDetails] = useState([]);
  const [open, setOpen] = useState(false);
  const [accountName, setAccountName] = useState("");
  const [fullBankName, setFullBankName] = useState("");
  const [state, setState] = useState({
    accountNumber: "",
    bankCode: "",
    withdrawAmount: "",
  });
  const [banks, setBanks] = useState([]);

  console.log("type", type);
  const { bankList, accounts } = useSelector((state) => state.wallet);

  useEffect(() => {
    setAddingAccount(true);
    if (bankList && bankList.length > 0) {
      setBanks(bankList);
    } else {
      setBanks([]);
    }
    setAddingAccount(false);
  }, [bankList]);

  useEffect(() => {
    setAddingAccount(true);
    Promise.all([
      getBanksList(dispatch),
      getMyAccounts(dispatch, 1, 10),
    ]).finally(() => {
      setAddingAccount(false);
    });
  }, [dispatch]);

  useEffect(() => {
    const fetchBank = async () => {
      try {
        const response = await getBankAccount(dispatch);
        console.log(response.status);
        setStatus(response.data);
        setAccountDetails(response.payload);
        if (response.payload && response.payload.bank_code) {
          getBankNameFromList(response.payload.bank_code);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchBank();
  }, []);

  const handleChange = ({ target: { id, value } }) => {
    const newValue = {};
    newValue[id] = value;
    setState({ ...state, ...newValue });
  };

  const handleSelect = ({ target: { name, value } }) => {
    const changed = {};
    changed[name] = value;
    getBankNameFromList(value);
    setState({ ...state, ...changed });
  };

  const getBankNameFromList = (bankCode) => {
    if (bankList && banks.length > 0) {
      const bank = banks.find((bank) => bank.code === bankCode);
      if (bank) {
        setFullBankName(bank.name);
      } else {
        setFullBankName("Bank not found");
      }
    }
  };

  const getAccountName = (evt) => {
    if (!state.bankCode && !state.accountNumber) {
      alert("Enter Account Number and Select Bank Name");
    } else {
      evt.preventDefault();
      const PAYSTACK_API_KEY = process.env.REACT_APP_PAYSTACK_API_KEY;
      const url = `https://api.paystack.co/bank/resolve?account_number=${state.accountNumber}&bank_code=${state.bankCode}`;
      setLoading(true);
      try {
        fetch(url, {
          method: "get",
          headers: new Headers({ Authorization: `Bearer ${PAYSTACK_API_KEY}` }),
        })
          .then((result) => result.json())
          .then((res) => {
            setLoading(false);
            res.status ? setAccountName(res.data.account_name) : alert(res.message);
          })
          .catch(() => setLoading(false));
      } catch (error) {
        setLoading(false);
      }
    }
  };

  const addAccount = async (evt) => {
    evt.preventDefault();
    setAddingAccount(true);
    resetMyAccounts(dispatch);
    setLoading(true);
    const payload = {
      account_name: accountName,
      account_number: state.accountNumber,
      bank: fullBankName,
      bankCode: state.bankCode,
    };
    Promise.resolve(createBankAccount(dispatch, payload)).finally(() => {
      getMyAccounts(dispatch, 1, 10);
      setAddingAccount(false);
      setState({ accountNumber: "", bankCode: "" });
      setLoading(false);
      setAccountName("");
      setFullBankName("");
    });
  };

  const handleAddAccount = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const refreshList = () => {
    resetMyAccounts(dispatch);
    getMyAccounts(dispatch, 1, 10);
  };

  const handleWithdraw = async () => {
    if (!state.withdrawAmount || state.withdrawAmount <= 0) {
      alert("Please enter a valid amount to withdraw");
      return;
    }
    setLoading(true);
    try {
      await withdrawFromWallet(dispatch, {
        amount: state.withdrawAmount,
        type: type,
        currency: type === "jaraepay" ? "NGN" : "USD",
      });
      setState({ ...state, withdrawAmount: "" });
    } catch (error) {
      alert(error.message || "Failed to process withdrawal");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={classes.root}>

      {/* Header */}
      <div className={classes.header}>
        <div className={classes.title}>Withdrawals</div>
        <div className={classes.subtitle}>Withdraw funds to your linked bank account</div>
      </div>

      {/* Bank details card */}
      {accountDetails && accountDetails?.account_number ? (
        <div className={classes.detailsCard}>

          {/* Bank avatar + name row */}
          <div className={classes.bankRow}>
            <div className={classes.bankAvatar}>
              {accountDetails.bank?.substring(0, 2).toUpperCase()}
            </div>
            <div>
              <div className={classes.bankName}>{accountDetails.bank}</div>
              <div className={classes.bankNum}>
                {"**** " + String(accountDetails.account_number).slice(-4)}
              </div>
            </div>
          </div>

          <div className={classes.divider} />

          {/* Read-only fields */}
          <div className={classes.fieldGroup}>
            <div className={classes.fieldRow}>
              <span className={classes.fieldLabel}>Account name</span>
              <span className={classes.fieldValue}>{accountDetails.account_name}</span>
            </div>
            <div className={classes.fieldRow}>
              <span className={classes.fieldLabel}>Account number</span>
              <span className={classes.fieldValue}>{accountDetails.account_number}</span>
            </div>
            <div className={classes.fieldRow}>
              <span className={classes.fieldLabel}>Bank</span>
              <span className={classes.fieldValue}>{accountDetails.bank}</span>
            </div>
          </div>

          <div className={classes.divider} />

          {/* Withdraw amount */}
          <div className={classes.amountSection}>
            <div className={classes.amountLabel}>Amount to withdraw</div>
            <CustomInput
              type="number"
              name="withdrawAmount"
              label=""
              id="withdrawAmount"
              value={state.withdrawAmount}
              onChange={handleChange}
              isRequired
              placeholder="Enter amount"
            />
            <CustomButton
              backgroundColor="#3A6ED4"
              color="#fff"
              text="Withdraw"
              additionalClass="mt-2"
              handleClick={handleWithdraw}
              loading={loading}
            />
          </div>
        </div>
      ) : (
        <div className={classes.emptyWrap}>
          <div className={classes.emptyIcon}>
            <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
              <rect x="2" y="6" width="20" height="14" rx="3" stroke="#3A6ED4" strokeWidth="1.5"/>
              <path d="M2 10h20" stroke="#3A6ED4" strokeWidth="1.5"/>
              <circle cx="6" cy="15" r="1" fill="#3A6ED4"/>
            </svg>
          </div>
          <div className={classes.emptyText}>No bank account linked yet</div>
          <div className={classes.emptyHint}>Add an account to start withdrawing</div>
        </div>
      )}

      {/* Add account button */}
      {!accountDetails?.account_number && (
        <CustomButton
          backgroundColor="#3A6ED4"
          color="#fff"
          text="Add Account"
          additionalClass="mt-3 mb-2"
          handleClick={handleAddAccount}
        />
      )}

      {/* Add account dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="add-account-dialog"
        PaperProps={{
          style: {
            width: "420px",
            borderRadius: "14px",
            padding: "8px",
          },
        }}
      >
        <DialogContent>
          <StripeAddAccount handleClose={handleClose} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

const useStyles = makeStyles({
  root: {
    padding: "24px",
  },
  header: {
    marginBottom: "20px",
  },
  title: {
    fontSize: "17px",
    fontWeight: 600,
    color: "#032250",
  },
  subtitle: {
    fontSize: "13px",
    color: "#7F91A7",
    marginTop: "3px",
  },
  detailsCard: {
    background: "#FFFFFF",
    border: "1px solid #EFEFEF",
    borderRadius: "12px",
    boxShadow: "0px 1px 4px rgba(0,0,0,0.06)",
    overflow: "hidden",
  },
  bankRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    padding: "16px 18px",
  },
  bankAvatar: {
    width: "42px",
    height: "42px",
    borderRadius: "10px",
    background: "#EAF0FB",
    color: "#3A6ED4",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "13px",
    fontWeight: 700,
    flexShrink: 0,
  },
  bankName: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#032250",
  },
  bankNum: {
    fontSize: "12px",
    color: "#7F91A7",
    marginTop: "2px",
  },
  divider: {
    height: "1px",
    background: "#F3F4F6",
  },
  fieldGroup: {
    padding: "14px 18px",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },
  fieldRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  fieldLabel: {
    fontSize: "13px",
    color: "#7F91A7",
  },
  fieldValue: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#032250",
  },
  amountSection: {
    padding: "16px 18px",
  },
  amountLabel: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#032250",
    marginBottom: "8px",
  },
  emptyWrap: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    padding: "36px 20px",
    background: "#FAFBFF",
    border: "1px dashed #C7D6F5",
    borderRadius: "12px",
    gap: "6px",
  },
  emptyIcon: {
    width: "56px",
    height: "56px",
    borderRadius: "50%",
    background: "#EAF0FB",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: "6px",
  },
  emptyText: {
    fontSize: "14px",
    fontWeight: 600,
    color: "#032250",
  },
  emptyHint: {
    fontSize: "12px",
    color: "#7F91A7",
  },
});

export default Withdraw;